import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Checkbox,
  Chip,
  ErrorState,
  Field,
  FormActions,
  FormSection,
  Input,
  Modal,
  Nota,
  PageHeader,
  Recolhivel,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import HistoricoEdicoes from '../../../components/ti/HistoricoEdicoes';
import ResumoRegistro from '../../../components/ti/ResumoRegistro';

import { TIBIKE, TIBIKES } from '../routes';
import {
  BikeEletricaHistorico,
  BikeMarca,
  SalvarBikeEletrica,
  atualizarBikeEletrica,
  buscarBikeEletrica,
  criarBikeEletrica,
  criarBikeMarca,
  historicoBikeEletrica,
  listarBikeMarcas,
} from '../../../api/bike-eletrica/bikeEletricaApi';
import { digitarMoeda, normalizarMoeda, numeroParaMoeda, parseMoeda } from '../../../utils/moeda';
import { codigoFipeValido, formatCodigoFipe } from '../../../utils/mascaras';
import { formatarDataCurta } from '../../../utils/data';
import { rotuloCodigoSga, rotuloOrigemBike, rotuloSituacaoBike } from '../../../utils/bikeRegras';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * A ficha de uma motocicleta elétrica — **cria e edita com o mesmo formulário**
 * (at-69, frente D/D7).
 *
 * Obrigatórios apenas **marca, modelo e valor** (decisão de Victor na at-45): os
 * campos técnicos ficam num bloco recolhido, todos opcionais, e **nenhum deles
 * entra em cálculo** — cota, rateio e produtos saem do valor protegido do veículo.
 *
 * ## O que mudou em relação ao modal de 509 linhas
 *
 *   - **Erro de carga vira `ErrorState`**, não `toast` + fechar. O modal antigo
 *     sumia da tela e o TI não sabia se tinha clicado errado.
 *   - **Validação no submit, junto do campo.** Marca/modelo/valor faltando só
 *     desabilitavam o botão, em silêncio (o defeito que a spec 03 §4 nomeia).
 *   - **"Nova marca" virou um diálogo NESTA ficha** (§3.2): antes ele fechava o
 *     editor e abria o painel da lista — o TI perdia tudo o que já tinha digitado.
 *   - **O texto do código fictício continua na tela** (D8/at-46), agora numa
 *     `Nota` que muda de variante conforme o campo esteja preenchido ou vazio.
 *
 * ⚠️ **O payload sai idêntico ao do modal** (D7): `Number(valor.toFixed(2))`,
 * `codigoFipeSga` vazio → `null` na edição e **omitido** na criação, e os técnicos
 * vazios viram `undefined` (o backend trata ausente como "não informado").
 */

type Estado = 'carregando' | 'ok' | 'erro';
type Aba = 'dados' | 'historico';

type CamposTecnicos = {
  anoModelo: string;
  potenciaW: string;
  bateriaWh: string;
  autonomiaKm: string;
  velocidadeMaxKmh: string;
};

const TECNICOS_VAZIOS: CamposTecnicos = {
  anoModelo: '',
  potenciaW: '',
  bateriaWh: '',
  autonomiaKm: '',
  velocidadeMaxKmh: '',
};

const TECNICOS: { campo: keyof CamposTecnicos; rotulo: string; maxLength: number }[] = [
  { campo: 'anoModelo', rotulo: 'Ano do modelo', maxLength: 4 },
  { campo: 'potenciaW', rotulo: 'Motor (W)', maxLength: 5 },
  { campo: 'bateriaWh', rotulo: 'Bateria (Wh)', maxLength: 6 },
  { campo: 'autonomiaKm', rotulo: 'Autonomia (km)', maxLength: 4 },
  { campo: 'velocidadeMaxKmh', rotulo: 'Vel. máxima (km/h)', maxLength: 3 },
];

const ID_BASE = 'bike-ficha';

const ABAS = [
  { id: 'dados', rotulo: 'Dados' },
  { id: 'historico', rotulo: 'Histórico' },
];

const paraTexto = (valor: number | null | undefined): string =>
  valor === null || valor === undefined ? '' : String(valor);

/** String vazia vira `undefined`: o backend trata ausente como "não informado". */
const numeroOuIndefinido = (texto: string): number | undefined => {
  const limpo = texto.trim();
  if (limpo === '') return undefined;
  const numero = Number(limpo);
  return Number.isFinite(numero) ? numero : undefined;
};

const BikeFichaPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(TIBIKES());
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const modo: 'criar' | 'editar' = !id || id === 'novo' ? 'criar' : 'editar';

  const [estado, setEstado] = useState<Estado>(modo === 'criar' ? 'ok' : 'carregando');
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState<Aba>('dados');

  const [marcas, setMarcas] = useState<BikeMarca[]>([]);
  /* A marca inicial vem por QUERY (`?marca=`), não por `location.state`: ela
     sobrevive a um F5 e a um link colado, que é o ponto da sub-rota. */
  const [marcaId, setMarcaId] = useState(searchParams.get('marca') ?? '');
  const [modelo, setModelo] = useState('');
  /** Mascarado durante a edição; convertido com `parseMoeda` só no envio. */
  const [valor, setValor] = useState('');
  const [codigoFipeSga, setCodigoFipeSga] = useState('');
  const [tipoLegal, setTipoLegal] = useState<'BICICLETA_ELETRICA' | 'CICLOMOTOR'>(
    'BICICLETA_ELETRICA',
  );
  const [tecnicos, setTecnicos] = useState<CamposTecnicos>(TECNICOS_VAZIOS);
  const [observacoes, setObservacoes] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [origem, setOrigem] = useState<'SEED' | 'MANUAL'>('MANUAL');
  const [atualizadoEm, setAtualizadoEm] = useState<string | null>(null);

  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<Record<string, string>>({});

  const [novaMarcaAberta, setNovaMarcaAberta] = useState(false);
  const [nomeNovaMarca, setNomeNovaMarca] = useState('');
  const [criandoMarca, setCriandoMarca] = useState(false);
  const [erroNovaMarca, setErroNovaMarca] = useState('');

  const [historico, setHistorico] = useState<BikeEletricaHistorico[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState('');

  /* As marcas vêm sempre completas: a atual pode estar inativa e precisa aparecer
     no seletor, senão a edição "perderia" a marca do registro. */
  const carregarMarcas = useCallback(async () => {
    try {
      setMarcas(await listarBikeMarcas({ incluirInativas: true }));
    } catch {
      // Acessório na carga: o seletor fica vazio e a validação do submit cobre.
    }
  }, []);

  const carregar = useCallback(async () => {
    if (modo === 'criar') return;

    setEstado('carregando');
    try {
      const bike = await buscarBikeEletrica(id as string);
      setMarcaId(bike.marcaId);
      setModelo(bike.modelo);
      // Float do banco → string mascarada (nunca `toString()` cru: os parsers de
      // moeda leriam "9500" como 95,00 — armadilha da at-30).
      setValor(numeroParaMoeda(bike.valorReferencia));
      setCodigoFipeSga(bike.codigoFipeSga ?? '');
      setTipoLegal(bike.tipoLegal);
      setTecnicos({
        anoModelo: paraTexto(bike.anoModelo),
        potenciaW: paraTexto(bike.potenciaW),
        bateriaWh: paraTexto(bike.bateriaWh),
        autonomiaKm: paraTexto(bike.autonomiaKm),
        velocidadeMaxKmh: paraTexto(bike.velocidadeMaxKmh),
      });
      setObservacoes(bike.observacoes ?? '');
      setAtivo(bike.ativo);
      setOrigem(bike.origem);
      setAtualizadoEm(bike.atualizadoEm);
      setEstado('ok');
    } catch (falha) {
      // 🔴 Era `toast.error` + `onClose()`: o modal sumia e o TI ficava sem saber
      // se tinha clicado errado. Agora a tela diz o que houve e oferece retentar.
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar a motocicleta elétrica.'));
      setEstado('erro');
    }
  }, [id, modo]);

  useEffect(() => {
    void carregarMarcas();
  }, [carregarMarcas]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const carregarHistorico = useCallback(async () => {
    if (modo === 'criar' || !id) return;

    setCarregandoHistorico(true);
    setErroHistorico('');
    try {
      setHistorico(await historicoBikeEletrica(id));
    } catch (falha) {
      setHistorico([]);
      setErroHistorico(extrairMensagemErro(falha, 'Falha ao ler o histórico.'));
    } finally {
      setCarregandoHistorico(false);
    }
  }, [id, modo]);

  useEffect(() => {
    if (aba === 'historico') void carregarHistorico();
  }, [aba, carregarHistorico]);

  const alterarTecnico = (campo: keyof CamposTecnicos, digitado: string) =>
    setTecnicos((atual) => ({ ...atual, [campo]: digitado.replace(/\D/g, '') }));

  const criarMarcaNaFicha = async () => {
    const nome = nomeNovaMarca.trim();
    if (!nome) {
      setErroNovaMarca('Informe o nome da marca.');
      return;
    }

    setCriandoMarca(true);
    setErroNovaMarca('');
    try {
      const criada = await criarBikeMarca(nome);
      await carregarMarcas();
      // Seleciona a criada: sem isto, o TI teria de procurá-la no seletor.
      setMarcaId(criada.id);
      setErros((atual) => ({ ...atual, marcaId: '' }));
      setNovaMarcaAberta(false);
      setNomeNovaMarca('');
      toast.success(`Marca "${criada.nome}" cadastrada`);
    } catch (falha) {
      setErroNovaMarca(extrairMensagemErro(falha, 'Não foi possível cadastrar a marca.'));
    } finally {
      setCriandoMarca(false);
    }
  };

  const rolarAte = (campo: string) => {
    const alvo = document.querySelector<HTMLElement>(`[data-field="${campo}"]`);
    // Defensivo: `scrollIntoView` não existe no jsdom nem em WebView antiga, e uma
    // exceção aqui abortaria a validação — o erro nem chegaria a aparecer.
    if (typeof alvo?.scrollIntoView === 'function') {
      alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const salvar = async () => {
    const valorNumerico = parseMoeda(valor);
    const codigoLimpo = codigoFipeSga.trim();

    /* 🔴 Validação NO SUBMIT, junto do campo. Antes os três obrigatórios só
       desabilitavam o botão — "não consigo salvar e não sei por quê" é o pior
       estado possível no celular (spec 03 §4). */
    const novos: Record<string, string> = {};
    if (!marcaId) novos.marcaId = 'Escolha a marca.';
    if (!modelo.trim()) novos.modelo = 'Informe o modelo.';
    if (Number.isNaN(valorNumerico) || valorNumerico <= 0) {
      novos.valor = 'Informe o valor de referência.';
    }
    if (!codigoFipeValido(codigoLimpo)) {
      novos.codigoFipeSga = 'Use o formato 000000-0 (6 dígitos, hífen, 1 dígito).';
    }

    setErros(novos);
    if (Object.keys(novos).length > 0) {
      rolarAte(Object.keys(novos)[0]);
      return;
    }

    const comuns: SalvarBikeEletrica = {
      tipoLegal,
      anoModelo: numeroOuIndefinido(tecnicos.anoModelo),
      potenciaW: numeroOuIndefinido(tecnicos.potenciaW),
      bateriaWh: numeroOuIndefinido(tecnicos.bateriaWh),
      autonomiaKm: numeroOuIndefinido(tecnicos.autonomiaKm),
      velocidadeMaxKmh: numeroOuIndefinido(tecnicos.velocidadeMaxKmh),
      observacoes: observacoes.trim() === '' ? undefined : observacoes.trim(),
    };

    setSalvando(true);
    try {
      if (modo === 'editar') {
        const atualizada = await atualizarBikeEletrica(id as string, {
          ...comuns,
          marcaId,
          modelo: modelo.trim(),
          valorReferencia: Number(valorNumerico.toFixed(2)),
          // `null` LIMPA o código (o backend trata `undefined` como "não mexer").
          codigoFipeSga: codigoLimpo === '' ? null : codigoLimpo,
          ativo,
        });

        toast.success('Motocicleta elétrica atualizada');
        setOrigem(atualizada.origem);
        setAtualizadoEm(atualizada.atualizadoEm);
        if (aba === 'historico') void carregarHistorico();
      } else {
        const criada = await criarBikeEletrica({
          ...comuns,
          marcaId,
          modelo: modelo.trim(),
          valorReferencia: Number(valorNumerico.toFixed(2)),
          ...(codigoLimpo !== '' ? { codigoFipeSga: codigoLimpo } : {}),
        });

        toast.success('Motocicleta elétrica cadastrada');
        navigate(TIBIKE(criada.id), { replace: true });
      }
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao salvar a motocicleta elétrica'));
    } finally {
      setSalvando(false);
    }
  };

  /* Ativas + a atual, mesmo inativa: senão a edição perderia a marca do registro. */
  const marcasSelecionaveis = marcas.filter((m) => m.situacao === 'ATIVO' || m.id === marcaId);
  const codigoLimpo = codigoFipeSga.trim();
  const codigoSga = rotuloCodigoSga(codigoLimpo === '' ? null : codigoLimpo);
  const cargaInicial = rotuloOrigemBike(origem);
  const situacao = rotuloSituacaoBike(ativo);
  const marcaAtual = marcas.find((m) => m.id === marcaId);

  const painelDados = () => (
    <div className="tw-flex tw-flex-col tw-gap-8">
      <FormSection titulo="Identificação" icone="identificacao">
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <Field
            rotulo="Marca"
            obrigatorio
            erro={erros.marcaId}
            campo="marcaId"
            className="tw-min-w-0 tw-flex-1 tw-basis-64"
          >
            <Select
              opcoes={marcasSelecionaveis.map((m) => ({
                valor: m.id,
                rotulo: `${m.nome}${m.situacao !== 'ATIVO' ? ' (inativa)' : ''}`,
              }))}
              placeholder="Selecione a marca"
              value={marcaId}
              onChange={(evento) => {
                setMarcaId(evento.target.value);
                setErros((atual) => ({ ...atual, marcaId: '' }));
              }}
            />
          </Field>

          <Botao variante="secundario" onClick={() => setNovaMarcaAberta(true)}>
            Nova marca
          </Botao>
        </div>

        <Field rotulo="Modelo" obrigatorio erro={erros.modelo} campo="modelo">
          <Input
            value={modelo}
            maxLength={120}
            placeholder="Ex.: E-Vibe Urbam"
            onChange={(evento) => {
              setModelo(evento.target.value);
              setErros((atual) => ({ ...atual, modelo: '' }));
            }}
          />
        </Field>
      </FormSection>

      <FormSection titulo="Valor de referência" icone="moeda">
        <Field
          rotulo="Valor de referência"
          obrigatorio
          dica="É este valor que pré-carrega o Valor Protegido na adesão. O consultor pode ajustar lá."
          erro={erros.valor}
          campo="valor"
        >
          <Input
            inputMode="decimal"
            value={valor}
            placeholder="Ex.: 9.500,00"
            // Digitação livre + normalização no blur (utils/moeda.ts, at-30):
            // máscara em centavos jogaria o cursor para o fim a cada tecla.
            onChange={(evento) => {
              setValor(digitarMoeda(evento.target.value));
              setErros((atual) => ({ ...atual, valor: '' }));
            }}
            onBlur={() => setValor(normalizarMoeda(valor))}
          />
        </Field>
      </FormSection>

      <FormSection titulo="Código no SGA" icone="enviar">
        <Field
          rotulo="Código FIPE fictício no SGA"
          dica="Formato 000000-0. Deixe vazio se o modelo ainda não foi cadastrado no SGA."
          erro={erros.codigoFipeSga}
          campo="codigoFipeSga"
        >
          <Input
            inputMode="numeric"
            value={codigoFipeSga}
            maxLength={8}
            placeholder="000000-0"
            onChange={(evento) => {
              setCodigoFipeSga(formatCodigoFipe(evento.target.value));
              setErros((atual) => ({ ...atual, codigoFipeSga: '' }));
            }}
          />
        </Field>

        {/* Texto preservado da at-46 (D8) — a explicação vive na tela, a pedido de
            Victor. A variante muda conforme o campo esteja preenchido ou vazio,
            que é o que o bloco colorido do modal antigo fazia. */}
        <Nota variante={codigoLimpo === '' ? 'atencao' : 'neutra'}>
          <strong>Como funciona:</strong> o SGA exige que todo veículo aponte para um código FIPE
          do catálogo dele — e motocicleta elétrica não tem FIPE. Por isso, para cada modelo, o TI{' '}
          <strong>cadastra um modelo fictício na tela do SGA</strong> (o mesmo que a operação já
          faz no SGA Consultor) e cola aqui o código que o SGA gerou.
          {codigoLimpo === '' ? (
            <>
              {' '}
              <strong>
                Sem este código a adesão fecha no SAAC, mas a motocicleta elétrica não sobe ao SGA
              </strong>{' '}
              — ela fica pendente até você preencher.
            </>
          ) : (
            <> Com o código preenchido, a adesão sobe ao SGA normalmente.</>
          )}
        </Nota>
      </FormSection>

      <Recolhivel titulo="Dados técnicos (opcionais)">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Nota>
            Nenhum destes campos entra em cálculo: cota, rateio e produtos saem do{' '}
            <strong>valor protegido</strong> do veículo. Eles servem para o TI documentar o produto.
          </Nota>

          <Field rotulo="Classificação legal" campo="tipoLegal">
            <Select
              /* ⚠️ at-54 (item 57): estes rótulos NÃO viram "motocicleta elétrica".
                 É a classificação LEGAL (CONTRAN 996/2023), e ali bicicleta
                 elétrica, ciclomotor e motocicleta são categorias distintas —
                 trocar tornaria o campo factualmente errado e deixaria as duas
                 opções com o mesmo nome. O rename do item 57 é do nome COMERCIAL
                 do produto, não desta taxonomia. */
              opcoes={[
                { valor: 'BICICLETA_ELETRICA', rotulo: 'Bicicleta elétrica' },
                { valor: 'CICLOMOTOR', rotulo: 'Ciclomotor' },
              ]}
              placeholder=""
              value={tipoLegal}
              onChange={(evento) => setTipoLegal(evento.target.value as typeof tipoLegal)}
            />
          </Field>

          <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
            {TECNICOS.map(({ campo, rotulo, maxLength }) => (
              <Field key={campo} rotulo={rotulo} campo={campo}>
                <Input
                  inputMode="numeric"
                  value={tecnicos[campo]}
                  maxLength={maxLength}
                  onChange={(evento) => alterarTecnico(campo, evento.target.value)}
                />
              </Field>
            ))}
          </div>

          <Field rotulo="Observações" campo="observacoes">
            <Textarea
              rows={3}
              value={observacoes}
              maxLength={2000}
              onChange={(evento) => setObservacoes(evento.target.value)}
            />
          </Field>
        </div>
      </Recolhivel>

      {modo === 'editar' && (
        <FormSection titulo="Situação" icone="alerta">
          <Checkbox
            rotulo="Ativa"
            destaque
            descricao="Desmarcar tira a motocicleta elétrica das listas de seleção da adesão — o catálogo nunca apaga."
            checked={ativo}
            onChange={setAtivo}
          />
        </FormSection>
      )}

      <FormActions>
        <Botao variante="secundario" onClick={voltar} disabled={salvando}>
          Cancelar
        </Botao>
        <Botao carregando={salvando} onClick={() => void salvar()}>
          Salvar
        </Botao>
      </FormActions>
    </div>
  );

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={6} altura="tw-h-12" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar a motocicleta elétrica."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (modo === 'criar') return painelDados();

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            <Chip variante={situacao.variante}>{situacao.texto}</Chip>
            <Chip variante={codigoSga.variante}>{codigoSga.texto}</Chip>
            {cargaInicial && <Chip variante={cargaInicial.variante}>{cargaInicial.texto}</Chip>}
          </div>

          {cargaInicial && <Nota variante="atencao">{cargaInicial.descricao}</Nota>}

          <ResumoRegistro
            linhas={[
              { rotulo: 'Marca', valor: marcaAtual?.nome ?? '—' },
              {
                rotulo: 'Última edição',
                valor: atualizadoEm ? formatarDataCurta(atualizadoEm) : '—',
              },
            ]}
          />
        </div>

        <Tabs
          idBase={ID_BASE}
          abas={ABAS}
          ativa={aba}
          aoTrocar={(valorAba) => setAba(valorAba as Aba)}
          rotuloAcessivel="Seções da motocicleta elétrica"
        />

        <div
          id={idDoPainel(ID_BASE)}
          role="tabpanel"
          aria-labelledby={idDaAba(ID_BASE, aba)}
          tabIndex={-1}
        >
          {aba === 'dados' ? (
            painelDados()
          ) : (
            <HistoricoEdicoes
              itens={historico.map((h) => ({
                id: h.id,
                quando: h.criadoEm,
                resumo: (
                  <>
                    {h.snapshotAnterior.marca} {h.snapshotAnterior.modelo} ·{' '}
                    <strong>
                      {/* ⚠️ `Number(...)`: o snapshot guarda o valor como STRING. */}
                      {numeroParaMoeda(Number(h.snapshotAnterior.valorReferencia))}
                    </strong>
                    {h.snapshotAnterior.ativo === false && ' · inativa'}
                  </>
                ),
              }))}
              carregando={carregandoHistorico}
              erro={erroHistorico || undefined}
              aoTentarNovamente={() => void carregarHistorico()}
              vazio="Nenhuma edição registrada até agora."
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo={modo === 'criar' ? 'Nova motocicleta elétrica' : modelo || 'Motocicleta elétrica'}
          sobretitulo="Motocicletas Elétricas"
          aoVoltar={voltar}
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-4xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            {conteudo()}
          </div>
        </div>
      </div>

      {/* "Nova marca" AQUI, e não na lista: o modal antigo fechava o editor e
          mandava o TI para o painel de marcas — perdendo tudo o que ele já tinha
          digitado na ficha (§3.2). */}
      <Modal
        aberto={novaMarcaAberta}
        titulo="Nova marca"
        descricao="A marca fica disponível para todos os modelos do catálogo."
        tamanho="sm"
        ocupado={criandoMarca}
        aoFechar={() => setNovaMarcaAberta(false)}
        alinharRodape="direita"
        rodape={
          <>
            <Botao
              variante="secundario"
              disabled={criandoMarca}
              onClick={() => setNovaMarcaAberta(false)}
            >
              Cancelar
            </Botao>
            <Botao carregando={criandoMarca} onClick={() => void criarMarcaNaFicha()}>
              Cadastrar marca
            </Botao>
          </>
        }
      >
        <Field rotulo="Nome da marca" obrigatorio erro={erroNovaMarca} campo="nomeNovaMarca">
          <Input
            autoFocus
            value={nomeNovaMarca}
            maxLength={80}
            placeholder="Ex.: Caloi"
            onChange={(evento) => {
              setNomeNovaMarca(evento.target.value);
              setErroNovaMarca('');
            }}
          />
        </Field>
      </Modal>
    </ShellSaac>
  );
};

export default BikeFichaPage;
